import { useEffect, useRef, useState } from 'react';

import { Redo, Undo } from '@mui/icons-material';
import { Button } from '@mui/material';
import cloneDeep from 'lodash/cloneDeep';
import { Control, FieldValues, UseFormReset, useWatch } from 'react-hook-form';

import strings from 'constants/index';
import { useDebounce } from 'hooks/common';
import { useFormContextHelper } from 'hooks/forms';

type HistoryButtonProps<TFieldValues extends FieldValues> = {
  control?: Control<TFieldValues>;
  reset?: UseFormReset<TFieldValues>;
  interval?: number;
};

function HistoryButtons<T>({ interval, ...props }: HistoryButtonProps<T>) {
  const [undoSteps, setUndoSteps] = useState([]);
  const pastSteps = undoSteps.slice(1);
  const [redoSteps, setRedoSteps] = useState([]);

  const { reset, control, trigger } = useFormContextHelper(props);
  const fields = useWatch({ control });
  const debounced = useDebounce(fields, interval || 100);
  const disableRecording = useRef(false);

  useEffect(() => {
    if (debounced && disableRecording.current === false) {
      setUndoSteps(current => [cloneDeep(debounced), ...current]);
      setRedoSteps([]);
    }
  }, [debounced]);

  return (
    <>
      <Button
        sx={{ mr: 1 }}
        data-testid="undo"
        onClick={() => {
          disableRecording.current = true;
          const currentItem = undoSteps[0];
          const targetItem = pastSteps[0];
          setUndoSteps(current => current.slice(1));
          setRedoSteps(current => [currentItem, ...current]);
          reset(targetItem);
          setTimeout(() => trigger(), 100); // re-validate after 100ms
          setTimeout(() => (disableRecording.current = false), 500);
        }}
        variant="contained"
        disabled={pastSteps.length === 0}
        startIcon={<Undo />}
      >
        {strings.undo}
      </Button>
      <Button
        data-testid="redo"
        onClick={() => {
          disableRecording.current = true;
          const targetItem = redoSteps[0];
          setRedoSteps(current => current.slice(1));
          setUndoSteps(current => [targetItem, ...current]);
          reset(targetItem);
          setTimeout(() => trigger(), 100); // re-validate after 100ms
          setTimeout(() => (disableRecording.current = false), 500);
        }}
        variant="contained"
        disabled={redoSteps.length === 0}
        startIcon={<Redo />}
      >
        {strings.redo}
      </Button>
    </>
  );
}

export default HistoryButtons;
